/** Typed-ish wrapper around the Ktor JSON API. Every call is same-origin and cookie-authenticated. */

export class ApiError extends Error {
  constructor(status, message, detail) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

async function request(path, { method = "GET", body, signal } = {}) {
  const response = await fetch(path, {
    method,
    signal,
    credentials: "same-origin",
    headers: body === undefined ? { Accept: "application/json" } : {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) return null;

  const text = await response.text();
  const payload = text ? safeParse(text) : null;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.error ?? payload?.message ?? `Request failed (${response.status})`,
      payload?.detail,
    );
  }
  return payload;
}

function safeParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  health: () => request("/api/health"),

  // --- session ---
  session: () => request("/api/session"),
  register: (payload) => request("/api/auth/register", { method: "POST", body: payload }),
  login: (payload) => request("/api/auth/login", { method: "POST", body: payload }),
  logout: () => request("/api/auth/logout", { method: "POST", body: {} }),
  verifyEmail: (token) => request("/api/auth/verify", { method: "POST", body: { token } }),
  resendVerification: (email) => request("/api/auth/resend-verification", { method: "POST", body: { email } }),
  forgotPassword: (email) => request("/api/auth/forgot-password", { method: "POST", body: { email } }),
  resetPassword: (token, password) =>
    request("/api/auth/reset-password", { method: "POST", body: { token, password } }),
  updateProfile: (payload) => request("/api/auth/profile", { method: "POST", body: payload }),

  config: () => request("/api/config"),

  // --- discovery ---
  home: () => request("/api/home"),
  facets: () => request("/api/restaurants/facets"),
  suggest: (q, signal) => request(`/api/restaurants/suggest?q=${encodeURIComponent(q)}`, { signal }),
  search: (params, signal) => request(`/api/restaurants?${params.toString()}`, { signal }),
  restaurant: (slug) => request(`/api/restaurants/${encodeURIComponent(slug)}`),
  postReview: (slug, payload) =>
    request(`/api/restaurants/${encodeURIComponent(slug)}/reviews`, { method: "POST", body: payload }),
  deleteReview: (id) => request(`/api/reviews/${encodeURIComponent(id)}`, { method: "DELETE" }),
  setFavorite: (slug, favorite) =>
    request(`/api/restaurants/${encodeURIComponent(slug)}/favorite`, { method: "POST", body: { favorite } }),

  // --- the visitor's own data ---
  myActivity: () => request("/api/me/activity"),
  getPreferences: () => request("/api/me/preferences"),
  mergePreferences: (patch) => request("/api/me/preferences", { method: "POST", body: patch }),
  replacePreferences: (data) => request("/api/me/preferences", { method: "PUT", body: data }),
  recordEvent: (event) => request("/api/me/events", { method: "POST", body: event }),

  // --- admin ---
  adminStats: () => request("/api/admin/stats"),
  adminRestaurants: (params) => request(`/api/admin/restaurants?${params.toString()}`),
  adminRestaurant: (id) => request(`/api/admin/restaurants/${encodeURIComponent(id)}`),
  createRestaurant: (payload) => request("/api/admin/restaurants", { method: "POST", body: payload }),
  updateRestaurant: (id, payload) =>
    request(`/api/admin/restaurants/${encodeURIComponent(id)}`, { method: "PUT", body: payload }),
  deleteRestaurant: (id) => request(`/api/admin/restaurants/${encodeURIComponent(id)}`, { method: "DELETE" }),
  adminUsers: (params) => request(`/api/admin/users?${params.toString()}`),

  // Ingest review queue — provider proposals awaiting a human decision.
  stagedUpdates: () => request("/api/admin/staged"),
  stagedSummary: () => request("/api/admin/staged/summary"),
  applyStaged: (id, fields, note) =>
    request(`/api/admin/staged/${encodeURIComponent(id)}/apply`, { method: "POST", body: { fields, note } }),
  rejectStaged: (id, note) =>
    request(`/api/admin/staged/${encodeURIComponent(id)}/reject`, { method: "POST", body: { note } }),
  ingestRestaurant: (id) =>
    request(`/api/admin/restaurants/${encodeURIComponent(id)}/ingest`, { method: "POST" }),
  matchQueue: () => request("/api/admin/matches"),
  sweepMatches: (limit) => request(`/api/admin/matches/sweep?limit=${limit}`, { method: "POST" }),
  acceptMatch: (candidateId) =>
    request(`/api/admin/matches/${encodeURIComponent(candidateId)}/accept`, { method: "POST" }),
  dismissMatch: (restaurantId) =>
    request(`/api/admin/matches/${encodeURIComponent(restaurantId)}/dismiss`, { method: "POST" }),
  ingestStatus: () => request("/api/admin/ingest/status"),
  staleMedia: (maxAgeDays) =>
    request(`/api/admin/ingest/media${maxAgeDays ? `?maxAgeDays=${maxAgeDays}` : ""}`),
  refreshMedia: (maxAgeDays, limit) =>
    request("/api/admin/ingest/media", { method: "POST", body: { maxAgeDays, limit } }),
  staleData: (maxAgeDays) =>
    request(`/api/admin/ingest/refresh${maxAgeDays ? `?maxAgeDays=${maxAgeDays}` : ""}`),
  refreshData: (maxAgeDays, limit, force = false) =>
    request("/api/admin/ingest/refresh", { method: "POST", body: { maxAgeDays, limit, force } }),
  runIngest: (town, limit, includeExcerpts, filters = {}) =>
    request("/api/admin/ingest/run", { method: "POST", body: { town, limit, includeExcerpts, ...filters } }),
  applyAllStaged: (kind) =>
    request(`/api/admin/staged/apply-all${kind ? `?kind=${kind}` : ""}`, { method: "POST" }),
  rejectAllStaged: (kind, note) =>
    request(`/api/admin/staged/reject-all${kind ? `?kind=${kind}` : ""}`, { method: "POST", body: { note } }),
  setUserRole: (id, role) =>
    request(`/api/admin/users/${encodeURIComponent(id)}/role`, { method: "PUT", body: { role } }),
  verifyUser: (id) => request(`/api/admin/users/${encodeURIComponent(id)}/verify`, { method: "PUT", body: {} }),
  deleteUser: (id) => request(`/api/admin/users/${encodeURIComponent(id)}`, { method: "DELETE" }),
  resendUserVerification: (id) =>
    request(`/api/admin/users/${encodeURIComponent(id)}/resend-verification`, { method: "POST", body: {} }),
};

export default api;
